import React, { useRef, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ClickableResultBox from "./ClickableResultBox";
const DecisionNode = ({
  number,
  topResultFromPrevious, // Value passed from the previous decision
  onResultClick,
  onHelpClick,
  setTotalDecisions,
  onSelectedChange,
  onDecisionMade,
}) => {
  const [selected, setSelected] = useState(null); // Track Yes/No selection
  const [questionData, setQuestionData] = useState({});
  const [totalDecisions, setLocalTotalDecisions] = useState(0); // Local state for total decisions
  const [hideDynamicLine, setHideDynamicLine] = useState(false); // State to manage DynamicLine visibility
  const [topBoxPosition, setTopBoxPosition] = useState({ top: "-2rem", left: "29%" }); // Track TopResultBox position
  const [cachedTop, setCachedTop] = useState(null); // Cache for `top` position
  const clickableBoxRefs = useRef([null, null]); // Refs for "Yes" and "No" ClickableResultBox

  useEffect(() => {
    const fetchQuestionData = async () => {
      const simulatedResponse = await new Promise((resolve) => {
        setTimeout(() => {
          const texts = {
            1: {
              question: "Is this material the same as in an existing commercially available device?",
              yesLabel: "YES",
              noLabel: "NO",
              clickableBoxText: {
                yes: "ISO 10993-1:2003(E) Material Characterization",
                no: "4.2.1 non-contact devices Biological Evaluation not applicable",
              },
            },
            2: {
              question: "Does this material comply with ISO 10993-5 cytotoxicity requirements?",
              yesLabel: "YES",
              noLabel: "NO",
              clickableBoxText: {
                yes: "ISO 10993-5 Cytotoxicity Testing Completed",
                no: "Further cytotoxicity testing required",
              },
            },
            3: {
              question: "Is the device used in a sterile environment?",
              yesLabel: "YES",
              noLabel: "NO",
              clickableBoxText: {
                yes: "ISO 10993-1:2003(E) Material Characterization",
                no: "4.2.1 non-contact devices Biological Evaluation not applicable",
              },
            },
            4: {
              question: "What is the duration of contact?",
              options: ["Limited (24h)", "Prolonged (24h-30d)", "Permanent (>30d)"], // Three options
              clickableBoxText: {
                limited: "Testing required for limited duration.",
                prolonged: "Testing required for prolonged duration.",
                permanent: "Testing required for permanent duration.",
              },
            },
          };

          resolve({ texts, total: Object.keys(texts).length });
        }, 500);
      });

      const { texts, total } = simulatedResponse;
      setQuestionData(texts[number]);
      setLocalTotalDecisions(total);
    };

    fetchQuestionData();
  }, [number]);

  useEffect(() => {
    if (totalDecisions > 0) {
      setTotalDecisions(totalDecisions);
    }
  }, [totalDecisions, setTotalDecisions]);

  useEffect(() => {
    if (number === totalDecisions && selected) {
      const selectedBoxRef =
        selected === "yes" ? clickableBoxRefs.current[0] : clickableBoxRefs.current[1];
      if (selectedBoxRef) {
        const rect = selectedBoxRef.getBoundingClientRect();
  
        // Cache `top` if not already cached, with additional offset
        const additionalOffset = 20; // Adjust this value to increase the top offset
        if (!cachedTop) {
          setCachedTop(`${rect.top + rect.height + window.scrollY + additionalOffset}px`);
        }
  
        // Dynamically calculate `left` for the selected box and adjust `top` with offset
        setTopBoxPosition({
          top: cachedTop || `${rect.top + rect.height + window.scrollY + additionalOffset}px`,
          left: `${rect.left + rect.width / 2 - 465}px`, // Center under the selected ClickableResultBox
        });
      }
      setHideDynamicLine(true); // Trigger fade-out for DynamicLineResult
    } else {
      // Reset for non-last decisions
      setTopBoxPosition({ top: "-2rem", left: "29%" });
      setCachedTop(null); // Clear cache for future decisions
    }
  }, [number, totalDecisions, selected, cachedTop]);
  
  const handleDecision = (decision) => {
    // Set the new selection and recalculate position
    setSelected(decision);

    // Notify the parent component about the selection
    if (onDecisionMade) {
      const resultText = questionData.clickableBoxText[decision.toLowerCase()];
      onDecisionMade(decision, resultText);
    }
  };

  const handleClickableBoxClick = () => {
    if (selected) {
      const resultText = questionData.clickableBoxText[selected.toLowerCase()];
      onResultClick(resultText);
    }
  };

  return (
    <Box 
  style={{ display: "flex", justifyContent: "center", flexDirection: "column" }} 
  position="relative"
>
  {/* TopResultBox with animation */}
  {number > 1 && topResultFromPrevious && (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{
          opacity: 1,
          scale: 1,
          top: topBoxPosition.top,
          left: topBoxPosition.left,
        }}
        transition={{
          duration: 0.5, // Smooth transition
          ease: "easeInOut",
        }}
        style={{
          position: "absolute",
          zIndex: 10,
          width: "250px",
          height: "120px",
        }}
      >
        <TopResultBox>{topResultFromPrevious}</TopResultBox>
      </motion.div>
      {/* DynamicLineResult with fade-out animation */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: hideDynamicLine ? 0 : 1 }}
        transition={{ duration: 0.3 }}
      >
        <DynamicLineResult />
      </motion.div>
    </>
  )}

  {/* Top Title */}
  <Decision>
    <DecisionNumber>Decision ({number})</DecisionNumber>
  </Decision>

  <NodeContainer number={number}>
    {/* Conditionally Render Two or Three Options */}
    {questionData.options ? (
      <ThreeColumnLayout>
        
        {questionData.options.map((option, index) => (
          <ClickableBox
            key={option}
            ref={(el) => (clickableBoxRefs.current[index] = el)}
            isSelected={selected === option}
            onClick={() => handleDecision(option)}
          >
            {option}
          </ClickableBox>
        ))}
      </ThreeColumnLayout>
    ) : (
      <>
        {/* Left Column: YES Button */}
        <LeftColumn>
          <ConnectorLineVerticalLeft isSelected={selected === "yes"} />
          <StyledButton
            ref={(el) => (clickableBoxRefs.current[0] = el)}
            variant={selected === "yes" ? "contained" : "outlined"}
            onClick={() => handleDecision("yes")}
            isSelected={selected === "yes"}
          >
            {questionData.yesLabel || "Yes"}
          </StyledButton>
          <LineAndArrow isSelected={selected === "yes"}>
            <VerticalLine isSelected={selected === "yes"} />
            <ArrowDown isSelected={selected === "yes"} />
          </LineAndArrow>
          <ClickableResultBox
            isSelected={selected === "yes"}
            number={number}
            onClick={handleClickableBoxClick}
            animate={{
              backgroundColor: selected === "yes" ? "#025131" : "#e0e0e0",
            }}
            transition={{ duration: 0.5 }}
          >
            {questionData.clickableBoxText?.yes || "Click to Proceed"}
          </ClickableResultBox>
          <DynamicLine />
        </LeftColumn>

        {/* Center Column: Question */}
        <CenterColumn>
          <ConnectorLineLeft isSelected={selected === "yes"} />
          <NodeWrapper>
            <QuestionBox selected={selected} number={number}>
              <Question>{questionData.question}</Question>
              <HelpButton onClick={onHelpClick}>?</HelpButton>
            </QuestionBox>
          </NodeWrapper>
          <ConnectorLineRight isSelected={selected === "no"} />
        </CenterColumn>

        {/* Right Column: NO Button */}
        <RightColumn>
          <ConnectorLineVerticalRight isSelected={selected === "no"} />
          <StyledButton
            ref={(el) => (clickableBoxRefs.current[1] = el)}
            variant={selected === "no" ? "contained" : "outlined"}
            onClick={() => handleDecision("no")}
            isSelected={selected === "no"}
          >
            {questionData.noLabel || "No"}
          </StyledButton>
          <LineAndArrow isSelected={selected === "no"}>
            <VerticalLine isSelected={selected === "no"} />
            <ArrowDown isSelected={selected === "no"} />
          </LineAndArrow>
          <ClickableResultBox
            isSelected={selected === "no"}
            number={number}
            onClick={handleClickableBoxClick}
            animate={{
              backgroundColor: selected === "no" ? "#025131" : "#e0e0e0",
            }}
            transition={{ duration: 0.5 }}
          >
            {questionData.clickableBoxText?.no || "Click to Proceed"}
          </ClickableResultBox>
          <DynamicLine />
        </RightColumn>
      </>
    )}
  </NodeContainer>
</Box>

  );
};
const ClickableBox = styled(Button)(({ isSelected, theme }) => ({
  flex: 1,
  backgroundColor: isSelected ? "#025131" : "#f5f5f5",
  color: isSelected ? "#fff" : "#303030",
  padding: "10px",
  border: `2px solid ${isSelected ? "#025131" : "#dcdcdc"}`,
  borderRadius: "8px",
  fontWeight: 700,
  textAlign: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: isSelected ? "#024026" : "#e8e8e8",
    borderColor: isSelected ? "#024026" : "#c5c5c5",
  },
}));

const TopResultBox = styled(Box)(({ theme }) => ({
  color: "#fff",
  backgroundColor: "#025131",
  padding: "5px",
  borderRadius: "4px",
  width: "250px",
  height: "120px",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "24px",
  fontFamily: "'Inter', sans-serif",
  cursor: "not-allowed",
  pointerEvents: "none",
}));
const NodeContainerThreeOptions = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyItems: "center",
  alignItems: "center",
  height: "100%",
  fontFamily: "Inter, sans-serif",
  position: "relative",
  marginTop: "-6.5rem", // Adjust margin to align properly
  [theme.breakpoints.up("md")]: {
    maxWidth: "1000px",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    width: "100%",
  },
  gap: "6rem", // Adjust gap for spacing
}));

const OptionsLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  maxWidth: "1000px",
  gap: "1rem",
  marginLeft: "0.3rem",
}));



const NodeContainer = styled(Box)(({ theme, number, isBottomTransition }) => ({
  display: "flex",
  gridTemplateColumns: "1fr 1fr 1fr",
  justifyItems: "center",
  alignItems: "center",
  height: "100%",
  fontFamily: "Inter, sans-serif",
  position: "relative",
  // Adjust marginTop dynamically for the last decision and when transitioning to the bottom
  marginTop: isBottomTransition && number === 3 ? "-4rem" : number === 1 ? "-4rem" : "5rem",
  [theme.breakpoints.up("md")]: {
    maxWidth: "1000px",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    width: "100%",
  },
}));



const LeftColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  marginRight: "-3.5rem",
  marginBottom: "-24rem",
  [theme.breakpoints.down("md")]: {
    marginBottom: "0", 
  },
}));
const LineAndArrow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
 
});
const VerticalLine = styled("div")(({ isSelected }) => ({
  width: "4px",
  height: "90px",
  backgroundColor: ` ${isSelected ? "#025131" : "#303030"}`,
}));
const ArrowDown = styled("div")(({ isSelected }) => ({
  width: "0",
  height: "0",
  borderLeft: "10px solid transparent",
  borderRight: "10px solid transparent",
  borderTop: `10px solid ${isSelected ? "#025131" : "#303030"}`,
  marginTop: "-2px",
  
}));

const CenterColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: 250,
  height: 250,
  position: "relative",
  [theme.breakpoints.down("md")]: {
    width: "100%",  // Full width for smaller screens
    marginBottom: "20px",  // Add spacing between items for smaller screens
  },
}));

const RightColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  marginBottom: "-24rem",
  marginLeft: "-3.5rem",
  [theme.breakpoints.down("md")]: {
    marginBottom: "0",  // Adjust margin for smaller screens
  },
}));



const StyledButton = styled(Button)(({ isSelected, theme }) => ({
  borderRadius: 4,
  backgroundColor: isSelected ? "#025131" : "transparent",
  color: isSelected ? "#fff" : "#303030",
  padding: "10px 20px",
  border: "1px solid #025131",
  cursor: "pointer",
  transition: "all 0.3s ease",
  fontWeight: 700,
  fontSize: 20,

  "&:hover": {
    backgroundColor: isSelected ? "#024026" : "rgba(2, 81, 49, 0.1)",
    borderColor: isSelected ? "#024026" : "#025131",
    color: isSelected ? "#fff" : "#025131",
  },
  "&:focus": {
    backgroundColor: isSelected ? "#024026" : "transparent",
    borderColor: isSelected ? "#024026" : "#025131",
    outline: "none", // Remove default browser focus outline
    color: isSelected ? "#fff" : "#025131", // Ensure color stays correct
  },
  "&:active": {
    backgroundColor: isSelected ? "#024026" : "rgba(2, 81, 49, 0.2)", // Active state styling
    borderColor: isSelected ? "#024026" : "#025131",
  },
}));





const NodeWrapper = styled(Box)(({ theme }) => ({
  width: '300px', 
  height: '300px',
  display: "flex",
  justifyContent: "flex-start", // Align content to the left horizontally
  alignItems: "flex-start",    // Align content to the top vertically
  position: "relative",
}));


const QuestionBox = styled(Box)(({ selected, number, theme }) => ({
  position: "relative",
  width: "250px",
  height: "250px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "30px",
  textAlign: "center",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: selected === null ? (number === 1 ? "#025131" : "#626262") : "#025131",
    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
    zIndex: -1,
  },
}));

const Question = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter, sans-serif",
  fontWeight: 700,
  fontSize: "20px", 
  color: "#ffffff",
  maxWidth: "130px", 
  lineHeight: "1.2em", 
  textAlign: "center",
}));


const HelpButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
 
  bottom: 0,
  margin: "0.4rem",
  backgroundColor: "transparent",
  color: "#fff",
  width: 30,
  height: 30,

  "&:hover": {
    backgroundColor: "#ffffff",
    color: "#025131",
  },
}));

const ConnectorLineLeft = styled(Box)(({ isSelected, theme }) => ({
  position: "absolute",
  height: "4px",
  width: "71px",
  backgroundColor: isSelected ? "#025131" : "#303030",
  top: "50%",
  right: "100%",
  transform: "translateY(-50%)",
}));

const ConnectorLineRight = styled(Box)(({ isSelected, theme }) => ({
  position: "absolute",
  height: "4px",
  width: "71px",
  backgroundColor: isSelected ? "#025131" : "#303030",
  top: "50%",
  left: "100%",
  transform: "translateY(-50%)",
}));

const ConnectorLineVerticalLeft = styled(Box)(({ isSelected, theme }) => ({
  position: "absolute",
  width: "4px",
  height: "55px",
  backgroundColor: isSelected ? "#025131" : "#303030",
  top: "0",
  transform: "translateY(-100%)",
}));

const ConnectorLineVerticalRight = styled(Box)(({ isSelected, theme }) => ({
  position: "absolute",
  width: "4px",
  height: "55px",
  backgroundColor: isSelected ? "#025131" : "#303030",
  top: "0",
  transform: "translateY(-100%)",
}));
const Decision = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start", // Center horizontally
  alignItems: "center", // Center vertically
  width: "100%",
  padding: "20px", // Add space above and below
  // Adjust margin to move closer to the node
}));

const DecisionNumber = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter, sans-serif",
  fontWeight: 700,
  fontSize: 20, // Increase font size slightly for emphasis
  lineHeight: "28px",
  color: "#303030",
  textAlign: "center", // Center text
  marginBottom: "10px", // Add margin to position it well above the decision node
}));

const LineConnectorWrapper = styled(Box)({
  position: "relative",
  height: "50px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const HorizontalLine = styled(Box)({
  width: "499px",
  height: "4px",
  backgroundColor: "#303030",
  position: "fixed",
  marginTop: '3.5rem',
});

const VerticalLineMiddle = styled(Box)({
  width: "4px",
  height: "60px",
  backgroundColor: "#303030",
  position: "absolute",
  top: "0",
  marginTop: '-2rem',
});

const NodeContainerThree = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyItems: "center",
  alignItems: "center",
  height: "100%",
  fontFamily: "Inter, sans-serif",
  position: "relative",
  marginTop: ".2rem",
  [theme.breakpoints.up("md")]: {
    maxWidth: "1000px",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    width: "100%",
  },
  gap: '8rem',
}));

const ThreeColumnLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  maxWidth: "1000px",
  gap: "1rem",
  marginLeft:'0.3rem'
}));
const DynamicLine = styled(Box)(({ isDashed, theme }) => ({
  position: "absolute",
  width: "4px",
  height: "60px",
  backgroundColor: "transparent",
  borderLeft: isDashed ? "4px dashed #025131" : "4px solid #303030",
  top: "100%",
  left: "50%",
  transform: "translateX(-50%)",
}));
const DynamicLineResult = styled(Box)(({ isDashed, theme }) => ({
  position: "absolute",
  width: "4px",
  height: "79px",
  backgroundColor: "transparent",
  borderLeft: isDashed ? "4px dashed #025131" : "4px solid #303030",
 marginTop:"5rem",
  left: "50.4%",
  transform: "translateX(-50%)",
}));
const DynamicLineThree = styled(Box)(({ isDashed, theme, selected, initial }) => ({
  height: "45px",
  backgroundColor: "transparent",
  borderLeft: isDashed ? "4px dashed #025131" : "4px solid #303030",
  marginTop: initial
    ? "-4.5rem"    // Initial state
    : selected
    ? "-4.5rem"  // Selected state
    : "-7.4rem", // Unselected state
  transform: "translateX(-50%)",
}));
const DynamicLineFour = styled(Box)(({ isDashed, theme, selected, initial }) => ({
  height: "60px",
  backgroundColor: "transparent",
  borderLeft: isDashed ? "4px dashed #025131" : "4px solid #303030",
  marginTop: initial
    ? "-4.5rem"    // Initial state
    : selected
    ? "-4.5rem"  // Selected state
    : "-7.4rem", // Unselected state
  transform: "translateX(-50%)",
}));
const DynamicLineThreeDashed = styled(Box)(({ isDashed, theme }) => ({

  height: "47px",
  backgroundColor: "transparent",
  borderLeft: isDashed ? "4px dashed #025131" : "4px solid #303030",
  marginTop: '0rem',
  transform: "translateX(-50%)",
}));

export default DecisionNode;
